import { useEffect, useState } from "react";

import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Select,
  Text,
} from "@chakra-ui/react";

import { GeneralInfo } from "../../../../types/registerBranchDataTypes";
import { useBranch } from "../../../../data/user";

interface generalStepProps {
  savedData: GeneralInfo;
  updateData: (data: GeneralInfo) => void;
  /** Receives the first input so the wizard can pass it to Modal `initialFocusRef`. */
  initialFocusRef?: React.Ref<HTMLInputElement>;
}

/** Eyebrow that groups a run of fields inside the grid. */
const FieldGroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <GridItem colSpan={{ base: 1, md: 3 }} pt={2}>
    <Text as="h4" textStyle="label" color="text.muted">
      {children}
    </Text>
  </GridItem>
);

const GeneralStep: React.FC<generalStepProps> = ({
  savedData,
  updateData,
  initialFocusRef,
}) => {
  const bussinesSectors = [
    "Bakery",
    "Butchery",
    "Cafe",
    "Catering",
    "Cinema",
    "Cloud Kitchen",
    "Clubs",
    "Coffee Shops",
    "Corporates",
    "Deli",
    "Ecommerce",
    "Entertainment",
    "Export",
    "Express",
    "Factories",
    " Fast Food",
    "Hotels",
    "Institution",
    "Lawsuits",
    "Others",
    "Petrol Station",
    "Private",
    "Pubs",
    "QSR",
    "Resort",
    "Restaurants",
    "Retail",
    "Sub Distributor",
    "Trading",
    "Travel",
    "Wholesale",
  ];

  const { branch } = useBranch();
  const [hasWebsite, setHasWebsite] = useState<boolean>(!!savedData.website_url && savedData.website_url !== 'none');
  const [hasServiceYears, setHasServiceYears] = useState(!!savedData.service_years && savedData.service_years > 0);

  // useEffect(() => {
  //   if (!hasWebsite && savedData.website_url !== 'none') {
  //     updateData({
  //       ...savedData,
  //       website_url: 'none',
  //     });
  //   }
  // }, [hasWebsite]);

  useEffect(() => {
    if (!hasServiceYears && savedData.service_years !== 0) {
      updateData({ ...savedData, service_years: 0 });
    }
    // deps intentionally limited to the toggle — savedData/updateData would re-run it on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasServiceYears]);

  return (
    <Box>
      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={5}>
        <FieldGroupLabel>Outlet</FieldGroupLabel>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Outlet Legal Name</FormLabel>
            <Input
              ref={initialFocusRef}
              type="text"
              isInvalid={savedData?.outlet_legal_name === ""} // validation
              value={savedData?.outlet_legal_name}
              placeholder={branch === "UAE"?"Name as per trade licence":""}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  outlet_legal_name: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Outlet Trade Name</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.outlet_trade_name === ""} // validation
              value={savedData?.outlet_trade_name}
              placeholder="Commecial Name"
              onChange={(e) => {
                updateData({
                  ...savedData,
                  outlet_trade_name: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Outlet Group Name</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.outlet_group_name === ""} // validation
              value={savedData?.outlet_group_name}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  outlet_group_name: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <FieldGroupLabel>Billing address</FieldGroupLabel>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl isRequired>
            <FormLabel>Billing Outlet Address</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.billing_outlet_address === ""} // validation
              value={savedData?.billing_outlet_address}
              placeholder={branch === "UAE"?"Street, Area, Bulding/Shop#, etc...":""}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  billing_outlet_address: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Billing Country</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.billing_country === ""} // validation
              value={savedData?.billing_country}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  billing_country: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Billing City</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.billing_city === ""} // validation
              value={savedData?.billing_city}
              placeholder={branch === "UAE"?"Dubai":""}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  billing_city: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Billing Phone</FormLabel>
            <Input
              type="number"
              isInvalid={savedData?.billing_phone === ""} // validation
              value={savedData?.billing_phone}
              placeholder={branch === "UAE"?"0501234567":""}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  billing_phone: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Billing PO Box</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.billing_po_box === ""} // validation
              value={savedData?.billing_po_box}
              placeholder={branch === "UAE"?"73021 (numbers only, no spaces)":""}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  billing_po_box: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 3 }}>
          <FormControl isRequired>
            <FormLabel>Billing Nearest Landmark</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.billing_nearest_landmark === ""} // validation
              value={savedData?.billing_nearest_landmark}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  billing_nearest_landmark: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <FieldGroupLabel>Delivery address</FieldGroupLabel>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl isRequired>
            <FormLabel>Delivery Outlet Address</FormLabel>
            <Input
              isInvalid={savedData?.delivery_outlet_address === ""} // validation
              value={savedData?.delivery_outlet_address}
              placeholder={branch === "UAE"?"Street, Area, Bulding/Shop#, etc...":""}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  delivery_outlet_address: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Delivery Country</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.delivery_country === ""} // validation
              value={savedData?.delivery_country}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  delivery_country: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Delivery City</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.delivery_city === ""} // validation
              value={savedData?.delivery_city}
              placeholder={branch === "UAE"?"Dubai":""}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  delivery_city: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Delivery Phone</FormLabel>
            <Input
              type="number"
              isInvalid={savedData?.delivery_phone === ""} // validation
              value={savedData?.delivery_phone}
              placeholder={branch === "UAE"?"0501234567":""}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  delivery_phone: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl isRequired>
            <FormLabel>Delivery PO Box</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.delivery_po_box === ""} // validation
              value={savedData?.delivery_po_box}
              placeholder={branch === "UAE"?"73021 (numbers only, no spaces)":""}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  delivery_po_box: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 3 }}>
          <FormControl isRequired>
            <FormLabel>Delivery Nearest Landmark</FormLabel>
            <Input
              type="text"
              isInvalid={savedData?.delivery_nearest_landmark === ""} // validation
              value={savedData?.delivery_nearest_landmark}
              onChange={(e) => {
                updateData({
                  ...savedData,
                  delivery_nearest_landmark: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <FieldGroupLabel>Business profile</FieldGroupLabel>
        <GridItem colSpan={{ base: 1, md: 3 }}>
          <FormControl>
            <Checkbox
              isChecked={hasServiceYears}
              onChange={(e) => setHasServiceYears(e.target.checked)}
              mb={2}
            >
              Been in service for more than 0 years?
            </Checkbox>
          </FormControl>
        </GridItem>

        <GridItem colSpan={{ base: 1, md: 3 }}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={5}>
            <GridItem colSpan={{ base: 1, md: hasServiceYears ? 2 : 3 }}>
              <FormControl isRequired>
                <FormLabel>Business Sector</FormLabel>
                <Select
                  isInvalid={savedData?.business_sector === ""}
                  value={savedData?.business_sector}
                  onChange={(e) => {
                    updateData({
                      ...savedData,
                      business_sector: e.target.value,
                    });
                  }}
                >
                  <option value="">Select</option>
                  {bussinesSectors.map((sector) => (
                    <option key={sector} value={sector}>
                      {sector}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            {hasServiceYears ? (
              <GridItem colSpan={1}>
                <FormControl isRequired>
                  <FormLabel>Service Years</FormLabel>
                  <Input
                    type="number"
                    isInvalid={!savedData?.service_years}
                    value={savedData?.service_years}
                    onChange={(e) => {
                    updateData({
                      ...savedData,
                      service_years: parseInt(e.target.value),
                    });
                    }}
                  />
                </FormControl>
              </GridItem>
            ) : (
              <Input type="hidden" value={0} onChange={() => {
              updateData({
                ...savedData,
                service_years: 0,
              });
              }} />
            )}
          </Grid>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 3 }}>
          <FormControl>
            <Checkbox
              isChecked={hasWebsite}
              onChange={(e) => {
                const checked = e.target.checked;
                setHasWebsite(checked);
                updateData({
                  ...savedData,
                  website_url: checked ? '' : 'none',
                });
              }}
              mb={2}
            >
              Has Website?
            </Checkbox>
            {hasWebsite ? (
              <>
                <FormLabel>Website URL</FormLabel>
                <Input
                  type="text"
                  isInvalid={!savedData?.website_url || savedData.website_url === 'none'}
                  value={savedData?.website_url === 'none' ? '' : savedData?.website_url}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    updateData({
                      ...savedData,
                      website_url: value || 'none',
                    });
                  }}
                />
              </>
            ) : (
              <Input type="hidden" value="none" />
            )}
          </FormControl>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default GeneralStep;
